import {
  Smartphone, Zap, RefreshCw, ShieldCheck,
  Leaf, Sparkles, CreditCard, Users, Truck,
} from "lucide-react";
import bambooHero from "@/assets/products/bamboo-hero.webp";
import bambooFeatures from "@/assets/products/bamboo-features.webp";
import bambooTeam from "@/assets/products/bamboo-team.webp";
import bambooProfile from "@/assets/products/bamboo-profile.webp";
import type { ProductPageDict } from "@/lib/shop/product-dict";

const SLUG = "bamboo";
const SKU = "BC-BAMBOO";
const BASE_PRICE = 60;
const PRODUCT_URL_EN = "https://banana.cards/shop/bamboo";
const PRODUCT_URL_VI = "https://banana.cards/vi/shop/bamboo";
const RATING = { value: "4.73", count: "132" };

const BUNDLES = [
  { qty: 1,  label: "1 card",  perCard: 1.0 },
  { qty: 3,  label: "3 cards", perCard: 0.9,  save: 10, badge: "Save 10%" },
  { qty: 5,  label: "5 cards", perCard: 0.82, save: 18, badge: "Most popular" },
  { qty: 10, label: "10 cards", perCard: 0.72, save: 28, badge: "Best value" },
] as const;

const BUNDLES_VI = [
  { qty: 1,  label: "1 thẻ",  perCard: 1.0 },
  { qty: 3,  label: "3 thẻ",  perCard: 0.9,  save: 10, badge: "Tiết kiệm 10%" },
  { qty: 5,  label: "5 thẻ",  perCard: 0.82, save: 18, badge: "Phổ biến nhất" },
  { qty: 10, label: "10 thẻ", perCard: 0.72, save: 28, badge: "Giá trị nhất" },
] as const;

const GALLERY = [
  { src: bambooHero,     alt: "Bamboo AI business card front and back with QR" },
  { src: bambooFeatures, alt: "Bamboo card features — custom brand colours, logo, NFC and QR" },
  { src: bambooProfile,  alt: "Customizable digital profiles shown on iPhone" },
  { src: bambooTeam,     alt: "Team & enterprise control dashboard" },
];

const GALLERY_VI = [
  { src: bambooHero,     alt: "Thẻ tre AI mặt trước và sau với mã QR" },
  { src: bambooFeatures, alt: "Cấu tạo thẻ tre — màu thương hiệu, logo, NFC và QR" },
  { src: bambooProfile,  alt: "Hồ sơ kỹ thuật số tùy chỉnh trên iPhone" },
  { src: bambooTeam,     alt: "Bảng điều khiển cho đội nhóm và doanh nghiệp" },
];

export const bambooDict: ProductPageDict = {
  slug: SLUG,
  routePath: "/shop/bamboo",
  productUrl: PRODUCT_URL_EN,
  basePrice: BASE_PRICE,
  sku: SKU,
  rating: RATING,
  seo: {
    title: "Bamboo Smart Business Card — banana.cards",
    description: "Sustainable bamboo NFC business card with AI-powered profile sharing, smart follow-ups and CRM. One tap shares your details — no app required.",
    ogTitle: "Bamboo Smart Business Card",
    ogDescription: "Natural bamboo NFC business card. Tap once, share instantly, capture every contact. AI follow-ups built in.",
  },
  jsonLdDescription:
    "The Bamboo AI Business Card is crafted from 100% natural, sustainably sourced bamboo and embedded with an NFC chip and printed QR linked to your AI-powered banana.cards profile.",
  eyebrow: "banana.cards · Bamboo",
  productName: "Bamboo AI Business Card",
  productIntro:
    "Sustainability meets smart technology. Crafted from natural bamboo — each card has a unique grain, no two ever the same. One tap shares your profile, tracks engagement and automates follow-ups. Update your details anytime without reprinting, while reducing paper waste and environmental impact.",
  ratingLabel: "4.73 · 132 reviews",
  perCardSuffix: "/ card · Free worldwide shipping over $50",
  saveBadge: (pct) => `Save ${pct}%`,
  bundles: BUNDLES,
  bundleSection: {
    eyebrow: "Bundle & save",
    heading: { pre: "Outfit your team in", em: "natural bamboo." },
    sub: "Each card carries its own profile — perfect for co-founders, sales teams or whole companies.",
    bundleLabel: "Bundle & save",
    perCardLabel: "per card",
    selected: "Selected",
    choose: "Choose",
  },
  usps: [
    { icon: Smartphone, label: "Share your details with one tap" },
    { icon: Zap,        label: "Collect their details back with one click" },
    { icon: RefreshCw,  label: "No app needed — works on both Apple & Android" },
    { icon: ShieldCheck,label: "Lifetime hardware guarantee" },
  ],
  features: {
    eyebrow: "Why bamboo",
    heading: { pre: "Naturally crafted.", em: "Digitally powerful." },
    items: [
      { icon: Leaf,       title: "100% sustainable bamboo", desc: "Natural, sustainably sourced bamboo — every card grain unique. Replaces thousands of paper cards." },
      { icon: Sparkles,   title: "AI follow-ups built-in",  desc: "Every tap lands in your banana CRM, enriched and ready for AI-drafted follow-ups." },
      { icon: CreditCard, title: "Custom brand & logo",     desc: "Engrave your logo, pick accent colors, and design a card that's unmistakably yours." },
      { icon: Users,      title: "Team-friendly",           desc: "Bulk bamboo orders for eco-conscious teams — branded, fulfilled and billed in one go." },
      { icon: Truck,      title: "Free worldwide shipping", desc: "Orders over $50 ship free to 100+ countries — 3-5 business days." },
      { icon: RefreshCw,  title: "30-day returns",          desc: "Not in love? Send it back within 30 days for a full refund." },
    ],
  },
  anatomy: {
    eyebrow: "Card anatomy",
    heading: { pre: "Every detail,", em: "engineered." },
    image: bambooFeatures,
    imageAlt: "Anatomy of the Bamboo AI business card — engraved logo, NFC chip and QR code",
    items: [
      { t: "Laser-engraved logo",      d: "Your logo etched directly onto the bamboo — permanent, premium, beautifully contrasted against the natural grain." },
      { t: "100% natural bamboo",      d: "Sustainably sourced, treated with a protective natural finish — resists moisture, scratches and warping." },
      { t: "Embedded NFC chip",        d: "Tap to share your full profile to any modern iPhone or Android — no app required." },
      { t: "Printed QR code",          d: "A perfect fallback for older devices or thicker phone cases — every introduction lands." },
    ],
  },
  profile: {
    eyebrow: "Your profile, your way",
    heading: { pre: "One card.", em: "Infinite identities." },
    body: "Pick a layout, add your colors, photo, links, services and CTAs — and switch between work, side-hustle or recruiting profiles from the same bamboo card.",
    bullets: [
      "12+ designer-made layouts & themes",
      "Add video, gallery, services, FAQs",
      "Multi-language profiles",
      "Lead-capture form on every card",
    ],
    image: bambooProfile,
    imageAlt: "Customisable digital profiles on iPhone",
  },
  compare: {
    eyebrow: "Paper vs Bamboo",
    heading: { pre: "Paper cards get lost.", em: "Bamboo gets remembered." },
    sub: "You deserve something that actually works — and that people actually keep.",
    paperLabel: "Paper business cards",
    productLabel: "Bamboo AI Business Card",
    paper: [
      "Hand out paper cards and hope they keep it",
      "Manually follow up days later",
      "Forget who you met or what you said",
      "No analytics, no CRM, no visibility",
      "You look like everyone else",
    ],
    product: [
      "Tap once, your details save instantly",
      "Automatic smart follow-ups",
      "Every contact captured automatically",
      "Real-time analytics show which events convert",
      "Modern, sustainable, unforgettable",
    ],
  },
  steps: {
    eyebrow: "How it works",
    heading: { pre: "You don't need to explain.", em: "You just tap." },
    sub: "They get everything that matters — instantly.",
    stepLabel: (n) => `Step ${n}`,
    items: [
      { title: "Tap once to share easily",   desc: "Tap your NFC Banana Card on any smartphone, no app required. Your profile, links and calendar open instantly." },
      { title: "They save you instantly",    desc: "They add you straight to their contacts — your info stays where they actually look: their phone." },
      { title: "You get their details back", desc: "Each new lead lands automatically in your Banana dashboard and syncs to your CRM — no manual entry." },
    ],
  },
  team: {
    eyebrow: "For teams",
    heading: { pre: "Built for", em: "eco-conscious teams." },
    body: "Roll out branded bamboo cards across your whole team, manage every profile from one dashboard, and route every new contact straight into your shared CRM.",
    cta: "Order for your team",
    image: bambooTeam,
    imageAlt: "Team and enterprise control dashboard",
    teamsHref: "/teams",
  },
  faq: {
    eyebrow: "FAQ",
    heading: { pre: "Frequently asked", em: "questions." },
    items: [
      { q: "What makes the Bamboo Smart Business Card eco-friendly?", a: "The Bamboo Smart Business Card is crafted from 100% natural, sustainably sourced bamboo. By replacing thousands of paper business cards with a single, reusable NFC card, you significantly reduce paper waste and your overall environmental footprint." },
      { q: "Is every Bamboo Smart Business Card unique?", a: "Yes. Because bamboo is a natural material, each card has its own grain pattern, texture and subtle color variation — no two are ever exactly alike." },
      { q: "How does the NFC technology work in a wooden card?", a: "The NFC antenna and chip are embedded beneath the surface of the bamboo during manufacturing. Bamboo is non-metallic, so it doesn't interfere with the signal — every tap reads instantly through the wood." },
      { q: "Can I customize the bamboo card with my logo?", a: "Absolutely. We laser-engrave your logo, name and accent details directly onto the bamboo surface. The engraving is permanent and won't fade." },
      { q: "How does the Banana AI help eco-conscious entrepreneurs?", a: "Beyond the sustainable card itself, our AI Brain automates follow-ups, drafts personalized emails and enriches every contact — so you spend less time on admin." },
      { q: "Is the Bamboo Smart Business Card durable enough for daily use?", a: "Yes. The bamboo is treated with a protective natural finish that resists moisture, scratches and warping — covered by our lifetime hardware guarantee." },
      { q: "Does the recipient need an app to see my details?", a: "No app needed. Your profile opens instantly in any modern browser on iOS or Android — they just tap their phone to your card or scan the QR code." },
      { q: "Can I link my bamboo card to my sustainable portfolio or website?", a: "Yes. Add unlimited links — portfolio, sustainability report, B-Corp certification, social profiles, booking page — and reorder them anytime without reprinting." },
      { q: "What happens to my data if I upgrade my plan?", a: "Nothing is lost. Your profile, contacts, analytics and integrations all carry over seamlessly when you upgrade." },
      { q: "How do I activate my new Bamboo Card?", a: "It's a 2-minute setup. Tap your card to your phone, follow the onboarding link, claim your profile, drop in your details and you're live." },
    ],
  },
  outro: {
    heading: { pre: "Network with", em: "nature on your side." },
    body: "Order before 3pm and we ship the next business day. Free worldwide shipping over $50.",
    primaryCta: "Get my Bamboo card",
    secondaryCta: "See all products",
    shopHref: "/shop",
  },
  buyBox: { addToCart: "Add to cart" },
  gallery: GALLERY,
};

export const bambooDictVi: ProductPageDict = {
  ...bambooDict,
  routePath: "/vi/shop/bamboo",
  productUrl: PRODUCT_URL_VI,
  seo: {
    title: "Thẻ Danh Thiếp Tre Thông Minh — banana.cards",
    description: "Thẻ NFC tre tự nhiên, bền vững, chia sẻ hồ sơ bằng AI, theo dõi thông minh và CRM. Một cú chạm để chia sẻ — không cần ứng dụng.",
    ogTitle: "Thẻ Danh Thiếp Tre Thông Minh",
    ogDescription: "Thẻ NFC tre tự nhiên. Chạm một lần, chia sẻ tức thì, thu thập mọi liên hệ. Tích hợp AI theo dõi tự động.",
    locale: "vi_VN",
  },
  jsonLdDescription:
    "Thẻ Danh Thiếp Tre AI được chế tác từ 100% tre tự nhiên, khai thác bền vững, gắn chip NFC và mã QR in trên thẻ liên kết hồ sơ banana.cards có AI hỗ trợ.",
  eyebrow: "banana.cards · Tre",
  productName: "Thẻ Danh Thiếp Tre AI",
  productIntro:
    "Sự bền vững gặp công nghệ thông minh. Chế tác từ tre tự nhiên — mỗi thẻ có vân gỗ riêng, không thẻ nào giống nhau. Một cú chạm chia sẻ hồ sơ, theo dõi tương tác và tự động hóa việc liên hệ lại. Cập nhật thông tin bất kỳ lúc nào mà không cần in lại.",
  ratingLabel: "4,73 · 132 đánh giá",
  perCardSuffix: "/ thẻ · Miễn phí vận chuyển toàn cầu cho đơn trên $50",
  saveBadge: (pct) => `Tiết kiệm ${pct}%`,
  bundles: BUNDLES_VI,
  bundleSection: {
    eyebrow: "Mua combo, tiết kiệm",
    heading: { pre: "Trang bị cho đội bằng", em: "tre tự nhiên." },
    sub: "Mỗi thẻ có hồ sơ riêng — hoàn hảo cho đồng sáng lập, đội bán hàng hoặc cả công ty.",
    bundleLabel: "Mua combo, tiết kiệm",
    perCardLabel: "mỗi thẻ",
    selected: "Đã chọn",
    choose: "Chọn",
  },
  usps: [
    { icon: Smartphone, label: "Chia sẻ thông tin chỉ với một cú chạm" },
    { icon: Zap,        label: "Thu lại thông tin của họ chỉ với một cú nhấp" },
    { icon: RefreshCw,  label: "Không cần ứng dụng — hoạt động trên cả iPhone và Android" },
    { icon: ShieldCheck,label: "Bảo hành phần cứng trọn đời" },
  ],
  features: {
    eyebrow: "Vì sao chọn tre",
    heading: { pre: "Chế tác tự nhiên.", em: "Sức mạnh số." },
    items: [
      { icon: Leaf,       title: "100% tre bền vững",          desc: "Tre tự nhiên, khai thác bền vững — mỗi thẻ có vân gỗ riêng. Thay thế hàng nghìn thẻ giấy." },
      { icon: Sparkles,   title: "AI theo dõi tích hợp",       desc: "Mỗi cú chạm đều được lưu vào CRM banana, làm giàu dữ liệu và sẵn sàng cho AI soạn lời theo dõi." },
      { icon: CreditCard, title: "Thương hiệu & logo tùy chỉnh", desc: "Khắc logo, chọn màu điểm nhấn, thiết kế thẻ đậm dấu ấn của bạn." },
      { icon: Users,      title: "Thân thiện đội nhóm",        desc: "Đặt số lượng lớn thẻ tre cho đội thân thiện môi trường — gắn thương hiệu, giao hàng và thanh toán cùng lúc." },
      { icon: Truck,      title: "Vận chuyển toàn cầu miễn phí", desc: "Đơn trên $50 được miễn phí giao đến hơn 100 quốc gia — 3-5 ngày làm việc." },
      { icon: RefreshCw,  title: "Đổi trả 30 ngày",            desc: "Không ưng? Gửi lại trong 30 ngày để hoàn tiền đầy đủ." },
    ],
  },
  anatomy: {
    eyebrow: "Cấu tạo thẻ",
    heading: { pre: "Mọi chi tiết,", em: "đều được tinh chỉnh." },
    image: bambooFeatures,
    imageAlt: "Cấu tạo Thẻ Danh Thiếp Tre AI — logo khắc, chip NFC và mã QR",
    items: [
      { t: "Logo khắc laser",          d: "Logo của bạn được khắc trực tiếp lên tre — vĩnh viễn, cao cấp, tương phản đẹp với vân gỗ tự nhiên." },
      { t: "100% tre tự nhiên",        d: "Khai thác bền vững, xử lý với lớp bảo vệ tự nhiên — chống ẩm, chống xước và chống cong vênh." },
      { t: "Chip NFC tích hợp",        d: "Chạm để chia sẻ toàn bộ hồ sơ với mọi iPhone hoặc Android hiện đại — không cần ứng dụng." },
      { t: "Mã QR in sẵn",             d: "Phương án dự phòng hoàn hảo cho thiết bị cũ hoặc ốp lưng dày — mọi cuộc gặp đều thành công." },
    ],
  },
  profile: {
    eyebrow: "Hồ sơ của bạn, theo cách của bạn",
    heading: { pre: "Một thẻ.", em: "Vô vàn bản dạng." },
    body: "Chọn bố cục, thêm màu sắc, ảnh, liên kết, dịch vụ và CTA — và chuyển đổi giữa hồ sơ công việc, dự án phụ hoặc tuyển dụng từ cùng một thẻ tre.",
    bullets: [
      "12+ bố cục & chủ đề thiết kế sẵn",
      "Thêm video, thư viện, dịch vụ, FAQ",
      "Hồ sơ đa ngôn ngữ",
      "Biểu mẫu thu khách tiềm năng trên mọi thẻ",
    ],
    image: bambooProfile,
    imageAlt: "Hồ sơ kỹ thuật số tùy chỉnh trên iPhone",
  },
  compare: {
    eyebrow: "Giấy vs Tre",
    heading: { pre: "Thẻ giấy bị thất lạc.", em: "Tre được ghi nhớ." },
    sub: "Bạn xứng đáng có thứ thực sự hoạt động — và người ta thực sự giữ lại.",
    paperLabel: "Thẻ danh thiếp giấy",
    productLabel: "Thẻ Danh Thiếp Tre AI",
    paper: [
      "Phát thẻ giấy và hy vọng họ giữ",
      "Theo dõi thủ công vài ngày sau",
      "Quên mất bạn đã gặp ai và nói gì",
      "Không phân tích, không CRM, không tầm nhìn",
      "Bạn trông giống mọi người khác",
    ],
    product: [
      "Chạm một lần, thông tin được lưu tức thì",
      "Theo dõi thông minh tự động",
      "Mọi liên hệ được thu thập tự động",
      "Phân tích thời gian thực cho biết sự kiện nào chuyển đổi",
      "Hiện đại, bền vững, khó quên",
    ],
  },
  steps: {
    eyebrow: "Cách hoạt động",
    heading: { pre: "Bạn không cần giải thích.", em: "Bạn chỉ cần chạm." },
    sub: "Họ nhận được mọi thứ quan trọng — ngay lập tức.",
    stepLabel: (n) => `Bước ${n}`,
    items: [
      { title: "Chạm một lần để chia sẻ",  desc: "Chạm thẻ Banana NFC vào bất kỳ điện thoại nào, không cần ứng dụng. Hồ sơ, liên kết và lịch của bạn mở ngay." },
      { title: "Họ lưu bạn tức thì",       desc: "Họ thêm bạn vào danh bạ — thông tin nằm đúng nơi họ thực sự nhìn: điện thoại của họ." },
      { title: "Bạn nhận lại thông tin",   desc: "Mỗi khách tiềm năng tự động vào bảng điều khiển Banana và đồng bộ CRM — không nhập tay." },
    ],
  },
  team: {
    eyebrow: "Dành cho đội nhóm",
    heading: { pre: "Xây cho", em: "đội thân thiện môi trường." },
    body: "Triển khai thẻ tre gắn thương hiệu cho cả đội, quản lý mọi hồ sơ trên một bảng điều khiển, định tuyến mọi liên hệ mới vào CRM chung.",
    cta: "Đặt cho đội của bạn",
    image: bambooTeam,
    imageAlt: "Bảng điều khiển đội và doanh nghiệp",
    teamsHref: "/teams",
  },
  faq: {
    eyebrow: "Câu hỏi thường gặp",
    heading: { pre: "Câu hỏi", em: "thường gặp." },
    items: [
      { q: "Thẻ Danh Thiếp Tre Thông Minh thân thiện môi trường ở điểm nào?", a: "Thẻ được chế tác từ 100% tre tự nhiên khai thác bền vững. Thay thế hàng nghìn thẻ giấy bằng một thẻ NFC tái sử dụng giúp giảm rác giấy và dấu chân môi trường." },
      { q: "Mỗi thẻ Tre có độc đáo không?", a: "Có. Vì tre là vật liệu tự nhiên, mỗi thẻ có vân gỗ, kết cấu và biến đổi màu nhẹ riêng — không thẻ nào giống nhau." },
      { q: "Công nghệ NFC hoạt động trong thẻ gỗ thế nào?", a: "Ăng-ten và chip NFC được nhúng dưới bề mặt tre trong quá trình sản xuất. Tre không chứa kim loại nên không cản trở tín hiệu." },
      { q: "Tôi có thể tùy chỉnh thẻ tre với logo không?", a: "Hoàn toàn được. Chúng tôi khắc laser logo, tên và chi tiết điểm nhấn trực tiếp lên bề mặt tre. Vĩnh viễn, không phai." },
      { q: "AI của Banana giúp doanh nhân thân thiện môi trường thế nào?", a: "Ngoài thẻ bền vững, AI Brain tự động hóa theo dõi, soạn email cá nhân hóa và làm giàu mỗi liên hệ — bạn dành ít thời gian hành chính hơn." },
      { q: "Thẻ Tre đủ bền cho sử dụng hàng ngày không?", a: "Có. Tre được xử lý với lớp bảo vệ tự nhiên chống ẩm, chống xước và chống cong vênh — bảo hành phần cứng trọn đời." },
      { q: "Người nhận cần ứng dụng để xem thông tin không?", a: "Không cần ứng dụng. Hồ sơ mở ngay trong trình duyệt hiện đại trên iOS hoặc Android." },
      { q: "Tôi có thể liên kết thẻ tre với portfolio bền vững của mình không?", a: "Có. Thêm liên kết không giới hạn — portfolio, báo cáo bền vững, chứng nhận B-Corp, hồ sơ mạng xã hội, trang đặt lịch — và sắp xếp lại bất kỳ lúc nào." },
      { q: "Dữ liệu của tôi sẽ ra sao nếu nâng cấp gói?", a: "Không mất gì. Hồ sơ, liên hệ, phân tích và tích hợp đều được chuyển liền mạch." },
      { q: "Làm sao kích hoạt thẻ Tre mới?", a: "Cài đặt 2 phút. Chạm thẻ vào điện thoại, theo liên kết onboarding, nhận hồ sơ, thêm thông tin và bạn đã sẵn sàng." },
    ],
  },
  outro: {
    heading: { pre: "Kết nối với", em: "thiên nhiên về phía bạn." },
    body: "Đặt trước 3 giờ chiều, chúng tôi giao vào ngày làm việc kế tiếp. Miễn phí vận chuyển toàn cầu cho đơn trên $50.",
    primaryCta: "Mua thẻ Tre",
    secondaryCta: "Xem tất cả sản phẩm",
    shopHref: "/shop",
  },
  buyBox: { addToCart: "Thêm vào giỏ" },
  gallery: GALLERY_VI,
};

const PRODUCT_URL_EL = "https://banana.cards/el/shop/bamboo";
const BUNDLES_EL = [
  { qty: 1, label: "1 κάρτα", perCard: 1.0 },
  { qty: 3, label: "3 κάρτες", perCard: 0.9, save: 10, badge: "Κέρδος 10%" },
  { qty: 5, label: "5 κάρτες", perCard: 0.82, save: 18, badge: "Δημοφιλέστερο" },
  { qty: 10, label: "10 κάρτες", perCard: 0.72, save: 28, badge: "Καλύτερη τιμή" },
] as const;
const GALLERY_EL = [
    { src: bambooHero, alt: "Ψηφιακή επαγγελματική κάρτα AI από μπαμπού — μπροστινή και πίσω όψη με NFC και QR" },
    { src: bambooFeatures, alt: "Χαρακτηριστικά κάρτας από μπαμπού — λογότυπο, εταιρικά χρώματα, NFC και QR" },
    { src: bambooProfile, alt: "Προσαρμόσιμα ψηφιακά προφίλ σε iPhone" },
    { src: bambooTeam, alt: "Πίνακας ελέγχου για ομάδες και επιχειρήσεις" }
  ];
export const bambooDictEl: ProductPageDict = {
  ...bambooDict,
  routePath: "/el/shop/bamboo",
  perCardSuffix: "/ κάρτα · Δωρεάν αποστολή παγκοσμίως για αγορές άνω των $50",
  bundleSection: { eyebrow: "Πακέτα με όφελος", heading: { pre: "Εξοπλίστε την ομάδα σας με κάρτες από", em: "φυσικό μπαμπού." }, sub: "Κάθε κάρτα έχει το δικό της προφίλ — ιδανική για συνιδρυτές, ομάδες πωλήσεων ή ολόκληρες εταιρείες.", bundleLabel: "Πακέτα με όφελος", perCardLabel: "ανά κάρτα", selected: "Επιλεγμένο", choose: "Επιλέξτε" },
  usps: [
    { icon: Smartphone, label: "Μοιραστείτε τα στοιχεία σας με ένα άγγιγμα" },
    { icon: Zap, label: "Λάβετε και τα δικά τους στοιχεία με ένα κλικ" },
    { icon: RefreshCw, label: "Χωρίς εφαρμογή — σε iPhone και Android" },
    { icon: ShieldCheck, label: "Εγγύηση εφ’ όρου ζωής για την κάρτα" }
  ],
  steps: { eyebrow: "Πώς λειτουργεί", heading: { pre: "Δεν χρειάζονται εξηγήσεις.", em: "Μόνο ένα άγγιγμα." }, sub: "Όλες οι σημαντικές πληροφορίες φτάνουν στα χέρια τους — αμέσως.", stepLabel: (n) => `Βήμα ${n}`, items: [
    { title: "Μοιραστείτε τα στοιχεία σας με ένα άγγιγμα", desc: "Αγγίζετε με την κάρτα NFC της Banana ένα smartphone, χωρίς να χρειάζεται εφαρμογή. Το προφίλ, οι σύνδεσμοι και το ημερολόγιό σας ανοίγουν αμέσως." },
    { title: "Σας αποθηκεύουν αμέσως στις επαφές", desc: "Σας προσθέτουν απευθείας στις επαφές τους — τα στοιχεία σας μένουν εκεί που τα αναζητούν: στο κινητό τους." },
    { title: "Λαμβάνετε και τα δικά τους στοιχεία", desc: "Κάθε νέα επαφή υποψήφιου πελάτη καταχωρίζεται αυτόματα στον πίνακα ελέγχου Banana και συγχρονίζεται με το CRM σας — χωρίς χειροκίνητη εισαγωγή." }
  ] },
  buyBox: { addToCart: "Προσθήκη στο καλάθι" },
  productUrl: PRODUCT_URL_EL,
  seo: { title: "Έξυπνη κάρτα από μπαμπού — banana.cards", description: "Κάρτα NFC από μπαμπού αειφόρου προέλευσης, με AI, έξυπνη επικοινωνία και CRM. Μοιραστείτε το προφίλ σας με ένα άγγιγμα — χωρίς εφαρμογή.", ogTitle: "Έξυπνη επαγγελματική κάρτα από μπαμπού", ogDescription: "Κάρτα NFC από φυσικό μπαμπού. Ένα άγγιγμα για άμεση κοινοποίηση και καταχώριση κάθε επαφής. Ενσωματωμένη επικοινωνία με AI.", locale: "el_GR" },
  jsonLdDescription: "Η ψηφιακή επαγγελματική κάρτα AI από μπαμπού κατασκευάζεται από 100% φυσικό μπαμπού αειφόρου προέλευσης, με ενσωματωμένο chip NFC και τυπωμένο QR που οδηγούν στο προφίλ σας στο banana.cards με υποστήριξη AI.",
  eyebrow: "banana.cards · Μπαμπού",
  productName: "Ψηφιακή επαγγελματική κάρτα AI από μπαμπού",
  productIntro: "Η βιωσιμότητα συναντά την έξυπνη τεχνολογία. Από φυσικό μπαμπού, με μοναδικά νερά σε κάθε κάρτα — καμία δεν είναι ίδια με άλλη. Ένα άγγιγμα κοινοποιεί το προφίλ σας, καταγράφει την αλληλεπίδραση και αυτοματοποιεί την επικοινωνία μετά τη γνωριμία. Ενημερώστε τα στοιχεία σας όποτε θέλετε, χωρίς νέα εκτύπωση, μειώνοντας τη σπατάλη χαρτιού και το περιβαλλοντικό σας αποτύπωμα.",
  ratingLabel: "4,73 · 132 αξιολογήσεις",
  saveBadge: (pct) => `Κέρδος ${pct}%`,
  bundles: BUNDLES_EL,
  features: { eyebrow: "Γιατί να επιλέξετε μπαμπού", heading: { pre: "Φυσική κατασκευή.", em: "Ψηφιακή δύναμη." }, items: [
    { icon: Leaf, title: "100% μπαμπού αειφόρου προέλευσης", desc: "Φυσικό μπαμπού αειφόρου προέλευσης, με μοναδικά νερά σε κάθε κάρτα. Αντικαθιστά χιλιάδες χάρτινες κάρτες." },
    { icon: Sparkles, title: "Ενσωματωμένη επικοινωνία με AI", desc: "Κάθε άγγιγμα καταχωρίζεται στο CRM της Banana, με εμπλουτισμένα στοιχεία επαφής, έτοιμα για μηνύματα συνέχειας που συντάσσει το AI." },
    { icon: CreditCard, title: "Το λογότυπο και η ταυτότητά σας", desc: "Χαράξτε το λογότυπό σας, επιλέξτε χρωματικές πινελιές και δημιουργήστε μια κάρτα με τη δική σας σφραγίδα." },
    { icon: Users, title: "Ιδανική για ομάδες", desc: "Μαζικές παραγγελίες καρτών από μπαμπού για ομάδες με οικολογική συνείδηση — με την εταιρική σας ταυτότητα, ενιαία αποστολή και τιμολόγηση." },
    { icon: Truck, title: "Δωρεάν αποστολή παγκοσμίως", desc: "Δωρεάν αποστολή για αγορές άνω των $50 σε 100+ χώρες — σε 3–5 εργάσιμες ημέρες." },
    { icon: RefreshCw, title: "Επιστροφές εντός 30 ημερών", desc: "Δεν σας ενθουσίασε; Επιστρέψτε την εντός 30 ημερών για πλήρη επιστροφή χρημάτων." }
  ] },
  anatomy: { eyebrow: "Η κατασκευή της κάρτας", heading: { pre: "Κάθε λεπτομέρεια,", em: "μελετημένη." }, image: bambooFeatures, imageAlt: "Ψηφιακή επαγγελματική κάρτα AI από μπαμπού — χαραγμένο λογότυπο, chip NFC και κωδικός QR", items: [
    { t: "Λογότυπο χαραγμένο με λέιζερ", d: "Το λογότυπό σας χαράσσεται απευθείας στο μπαμπού — μόνιμο, εκλεπτυσμένο και με όμορφη αντίθεση στα φυσικά νερά." },
    { t: "100% φυσικό μπαμπού", d: "Αειφόρου προέλευσης, με φυσική προστατευτική επίστρωση που αντιστέκεται στην υγρασία, στις γρατζουνιές και στο σκέβρωμα." },
    { t: "Ενσωματωμένο chip NFC", d: "Με ένα άγγιγμα μοιράζεστε ολόκληρο το προφίλ σας με κάθε σύγχρονο iPhone ή Android — χωρίς εφαρμογή." },
    { t: "Τυπωμένος κωδικός QR", d: "Η ιδανική εναλλακτική για παλαιότερες συσκευές ή πιο χοντρές θήκες κινητού — για να μη χάνετε καμία γνωριμία." }
  ] },
  profile: { eyebrow: "Το προφίλ σας, όπως το θέλετε", heading: { pre: "Μία κάρτα.", em: "Αμέτρητες ταυτότητες." }, bullets: [
    "12+ διατάξεις και θέματα από επαγγελματίες σχεδιαστές",
    "Προσθέστε βίντεο, συλλογή εικόνων, υπηρεσίες και συχνές ερωτήσεις",
    "Προφίλ σε πολλές γλώσσες",
    "Φόρμα συλλογής στοιχείων υποψήφιων πελατών σε κάθε κάρτα"
  ], imageAlt: "Προσαρμόσιμα ψηφιακά προφίλ σε iPhone", body: "Επιλέξτε διάταξη, προσθέστε χρώματα, φωτογραφία, συνδέσμους, υπηρεσίες και κουμπιά δράσης. Εναλλάξτε προφίλ για τη δουλειά, τα παράλληλα έργα ή τις προσλήψεις σας, από την ίδια κάρτα από μπαμπού.", image: bambooProfile },
  compare: { eyebrow: "Χαρτί ή μπαμπού;", heading: { pre: "Οι χάρτινες κάρτες χάνονται.", em: "Το μπαμπού μένει αξέχαστο." }, sub: "Αξίζετε κάτι που αποδίδει πραγματικά — και που οι άλλοι θέλουν να κρατήσουν.", paperLabel: "Χάρτινες επαγγελματικές κάρτες", productLabel: "Ψηφιακή επαγγελματική κάρτα AI από μπαμπού", paper: [
    "Μοιράζετε χάρτινες κάρτες και ελπίζετε να τις κρατήσουν",
    "Επικοινωνείτε ξανά χειροκίνητα, μέρες αργότερα",
    "Ξεχνάτε ποιον γνωρίσατε ή τι συζητήσατε",
    "Χωρίς αναλυτικά στοιχεία, CRM ή εικόνα των αποτελεσμάτων",
    "Δεν ξεχωρίζετε από τους υπόλοιπους"
  ], product: [
    "Ένα άγγιγμα αρκεί για άμεση αποθήκευση των στοιχείων σας",
    "Έξυπνη, αυτοματοποιημένη επικοινωνία μετά τη γνωριμία",
    "Αυτόματη καταχώριση κάθε επαφής",
    "Αναλυτικά στοιχεία σε πραγματικό χρόνο δείχνουν ποιες εκδηλώσεις φέρνουν πελάτες",
    "Σύγχρονη, βιώσιμη, αξέχαστη"
  ] },
  team: { eyebrow: "Για ομάδες", heading: { pre: "Σχεδιασμένη για", em: "ομάδες με οικολογική συνείδηση." }, body: "Εξοπλίστε όλη την ομάδα σας με κάρτες από μπαμπού με την εταιρική σας ταυτότητα. Διαχειριστείτε κάθε προφίλ από έναν πίνακα ελέγχου και στείλτε κάθε νέα επαφή απευθείας στο κοινό σας CRM.", cta: "Παραγγείλετε για την ομάδα σας", image: bambooTeam, imageAlt: "Πίνακας ελέγχου για ομάδες και επιχειρήσεις", teamsHref: "/teams" },
  faq: { eyebrow: "Συχνές ερωτήσεις", heading: { pre: "Οι πιο συχνές", em: "ερωτήσεις σας." }, items: [
    { q: "Τι κάνει την έξυπνη επαγγελματική κάρτα από μπαμπού οικολογική;", a: "Κατασκευάζεται από 100% φυσικό μπαμπού αειφόρου προέλευσης. Αντικαθιστώντας χιλιάδες χάρτινες επαγγελματικές κάρτες με μία επαναχρησιμοποιούμενη κάρτα NFC, μειώνετε σημαντικά τη σπατάλη χαρτιού και το συνολικό περιβαλλοντικό σας αποτύπωμα." },
    { q: "Είναι κάθε κάρτα από μπαμπού μοναδική;", a: "Ναι. Το μπαμπού είναι φυσικό υλικό, οπότε κάθε κάρτα έχει τα δικά της νερά, υφή και μικρές χρωματικές διαφορές — καμία δεν είναι ακριβώς ίδια με κάποια άλλη." },
    { q: "Πώς λειτουργεί το NFC σε μια ξύλινη κάρτα;", a: "Η κεραία και το chip NFC ενσωματώνονται κάτω από την επιφάνεια του μπαμπού κατά την κατασκευή. Επειδή το μπαμπού δεν είναι μεταλλικό, δεν εμποδίζει το σήμα — κάθε άγγιγμα διαβάζεται αμέσως μέσα από το ξύλο." },
    { q: "Μπορώ να προσθέσω το λογότυπό μου στην κάρτα από μπαμπού;", a: "Βεβαίως. Χαράσσουμε με λέιζερ το λογότυπο, το όνομα και τις διακοσμητικές λεπτομέρειες απευθείας στην επιφάνεια του μπαμπού. Η χάραξη είναι μόνιμη και δεν ξεθωριάζει." },
    { q: "Πώς βοηθά το AI της Banana τους επιχειρηματίες με οικολογική συνείδηση;", a: "Πέρα από την ίδια την κάρτα αειφόρου προέλευσης, το AI Brain αυτοματοποιεί την επικοινωνία μετά τη γνωριμία, συντάσσει εξατομικευμένα email και εμπλουτίζει τα στοιχεία κάθε επαφής — για να αφιερώνετε λιγότερο χρόνο σε διαχειριστικές εργασίες." },
    { q: "Αντέχει η κάρτα από μπαμπού στην καθημερινή χρήση;", a: "Ναι. Το μπαμπού διαθέτει φυσική προστατευτική επίστρωση που αντιστέκεται στην υγρασία, στις γρατζουνιές και στο σκέβρωμα. Καλύπτεται από την εγγύηση εφ’ όρου ζωής για την κάρτα." },
    { q: "Χρειάζεται εφαρμογή ο παραλήπτης για να δει τα στοιχεία μου;", a: "Δεν χρειάζεται εφαρμογή. Το προφίλ σας ανοίγει αμέσως σε κάθε σύγχρονο πρόγραμμα περιήγησης σε iOS ή Android — αρκεί ένα άγγιγμα του κινητού στην κάρτα σας ή μια σάρωση του κωδικού QR." },
    { q: "Μπορώ να συνδέσω την κάρτα με το χαρτοφυλάκιο βιωσιμότητας ή τον ιστότοπό μου;", a: "Ναι. Προσθέστε απεριόριστους συνδέσμους — χαρτοφυλάκιο, έκθεση βιωσιμότητας, πιστοποίηση B-Corp, κοινωνικά δίκτυα, σελίδα κρατήσεων — και αλλάξτε τη σειρά τους όποτε θέλετε, χωρίς νέα εκτύπωση." },
    { q: "Τι γίνεται με τα δεδομένα μου αν αναβαθμίσω το πακέτο μου;", a: "Δεν χάνεται τίποτα. Το προφίλ, οι επαφές, τα αναλυτικά στοιχεία και οι διασυνδέσεις σας μεταφέρονται απρόσκοπτα με την αναβάθμιση." },
    { q: "Πώς ενεργοποιώ τη νέα μου κάρτα από μπαμπού;", a: "Η ρύθμιση διαρκεί 2 λεπτά. Αγγίζετε με την κάρτα το κινητό σας, ακολουθείτε τον σύνδεσμο αρχικής ρύθμισης, κατοχυρώνετε το προφίλ σας, προσθέτετε τα στοιχεία σας και είστε έτοιμοι." }
  ] },
  outro: { heading: { pre: "Δικτυωθείτε με", em: "τη φύση στο πλευρό σας." }, body: "Παραγγείλετε πριν από τις 15:00 και αποστέλλουμε την επόμενη εργάσιμη ημέρα. Δωρεάν αποστολή παγκοσμίως για αγορές άνω των $50.", primaryCta: "Αποκτήστε την κάρτα σας από μπαμπού", secondaryCta: "Δείτε όλα τα προϊόντα", shopHref: "/shop" },
  gallery: GALLERY_EL
};
